/**
 * Centralized configuration module
 *
 * This module provides a unified API for loading and validating
 * environment-specific configuration for multi-environment authentication.
 *
 * Supports multiple identity providers (Cognito, Entra, etc.) through
 * provider-agnostic configuration.
 *
 * Usage:
 *   import { getConfig } from '@/config';
 *   const config = getConfig();
 *
 * For provider-agnostic configuration:
 *   import { loadAuthConfig, getAuthConfig, getOidcClientConfig } from '@/config/auth';
 */

// Export all types
export type {
  EnvironmentName,
  EnvironmentConfig,
  OidcConfig,
  ValidationResult,
  ApiTarget,
  DevModeConfig,
  DevModeContextValue,
  EnvironmentDisplayInfo
} from './types';
export { ENV_KEYS } from './types';

// Export configuration builders
export {
  buildLocalConfig,
  buildDevConfig,
  buildProdConfig,
  buildDeployedConfig,
  loadEnvironmentConfig
} from './environments';

// Export validation utilities
export { isValidUrl, validateConfig, validateEnvVars, getErrorMessage } from './validation';

// Export error classes
export {
  ConfigError,
  ConfigValidationError,
  EnvironmentNotFoundError,
  EnvironmentMismatchError,
  AuthProviderError
} from './errors';

// Export Cognito utilities (deprecated - use auth.ts exports instead)
export { toOidcConfig, validateOidcConfig, getDefaultOidcConfig } from './cognito';

// Export provider-agnostic auth configuration (preferred)
export {
  loadAuthConfig,
  getAuthConfig,
  getOidcClientConfig,
  type AuthConfig
} from './auth';

// Re-export types for convenience
import type { EnvironmentConfig, OidcConfig } from './types';
import { loadEnvironmentConfig } from './environments';
import { validateConfig } from './validation';
import { ConfigValidationError } from './errors';
import { toOidcConfig } from './cognito';

/**
 * Get validated environment configuration
 *
 * This is the main entry point for accessing configuration.
 * Loads the appropriate environment configuration, validates it,
 * and throws a descriptive error if validation fails.
 *
 * @throws {ConfigValidationError} if configuration is invalid
 * @returns {EnvironmentConfig} validated configuration
 */
export function getConfig(): EnvironmentConfig {
  // Load environment-specific configuration
  const config = loadEnvironmentConfig();

  // Validate configuration
  const validation = validateConfig(config);

  if (!validation.valid) {
    throw new ConfigValidationError(validation.errors);
  }

  return config;
}

/**
 * Get validated OIDC configuration
 *
 * Convenience function that combines getConfig() and toOidcConfig()
 * to provide ready-to-use OIDC configuration.
 *
 * @throws {ConfigValidationError} if configuration is invalid
 * @returns {OidcConfig} validated OIDC configuration
 */
export function getOidcConfig(): OidcConfig {
  const config = getConfig();
  return toOidcConfig(config);
}

/**
 * Check if running in mock mode
 *
 * Utility to determine if the application is using mock authentication
 * (local development without real Cognito).
 *
 * @returns {boolean} true if in mock mode
 */
export function isMockMode(): boolean {
  try {
    const config = getConfig();
    return config.isMock;
  } catch {
    // If config fails to load, assume not mock mode
    return false;
  }
}

/**
 * Check if dev mode (runtime API switching) is available
 *
 * Dev mode is available when:
 * 1. Not a deployed environment (VITE_AUTHORITY not set)
 * 2. Using dev environment (VITE_ENVIRONMENT === 'dev')
 *
 * @returns {boolean} true if dev mode features are available
 */
export function isDevModeAvailable(): boolean {
  const isDeployed = !!import.meta.env.VITE_AUTHORITY;
  const envName = import.meta.env.VITE_ENVIRONMENT as string;
  return !isDeployed && envName === 'dev';
}

/**
 * Get API URL for a specific target
 *
 * Helper function to get the configured API URL for a given target.
 * Used by useDevModeConfig hook and components.
 *
 * IMPORTANT: This function only validates the requested target's URL.
 * In deployed environments (VITE_AUTHORITY set), this function should not be called
 * as the API URL comes from VITE_API_URL instead.
 *
 * @param target - The API target ('local', 'dev', or 'prod')
 * @returns The API URL for the specified target
 * @throws Error if target requires an environment variable that is not set
 */
export function getApiUrlForTarget(target: 'local' | 'dev' | 'prod'): string {
  // Local always returns localhost - no env var needed
  if (target === 'local') {
    return 'http://localhost:8000';
  }

  // For dev/prod targets, validate the required environment variable
  if (target === 'dev') {
    const devApiUrl = import.meta.env.VITE_DEV_API_URL;
    if (!devApiUrl) {
      throw new Error(
        'VITE_DEV_API_URL is required but not set. ' +
        'Please add it to your .env file. See README "Using as a Template" section.'
      );
    }
    return devApiUrl;
  }

  if (target === 'prod') {
    const prodApiUrl = import.meta.env.VITE_PROD_API_URL;
    if (!prodApiUrl) {
      throw new Error(
        'VITE_PROD_API_URL is required but not set. ' +
        'Please add it to your .env file. See README "Using as a Template" section.'
      );
    }
    return prodApiUrl;
  }

  // Fallback for any unexpected target
  return 'http://localhost:8000';
}
